import { useMediaQuery } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../global-state/themeContext';
import { StoreDetails } from '../../../global-state/types';
import { Icon, Icons, Typography } from '../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../stories/atoms/containerV2/ContainerV2';
import { muiTheme } from '../../../theme';
import { areaResultTranslations } from '../../../translations/areaResult';
import { isStoreVisibilityType, StoreVisibility } from '../storeVisibility/StoreVisibility';
import { StoresViewWrapper, UnderlinedTypography } from './StoresView.styles';

type StoresViewProps = {
  stores: StoreDetails[];
  showStoreDetails: (storeId: string) => void;
};

export const StoresView: FC<StoresViewProps> = ({ stores, showStoreDetails }) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));
  const isSmallDesktop = useMediaQuery(muiTheme.breakpoints.down('desktop'));

  return (
    <StoresViewWrapper
      padding={[1, 0, 10]}
      margin={[5, 0, 0]}
      data-testid="stores-view-wrapper"
      isMobile={isMobile}
      isTablet={isTablet}
      isSmallDesktop={isSmallDesktop}
    >
      {stores.map((store) => (
        <ColumnFlexContainer
          key={store.storeId}
          data-testid={`store-${store.storeId}`}
          padding={[4]}
          borderRadius={[4]}
          shadow
          hasBorder
          alignItems="start"
          backgroundColor={mode === 'dark' ? 'backgroundPaperDark' : 'white'}
          borderColor={mode === 'dark' ? 'backgroundSelectedDark' : 'borderGray'}
          gap={[2]}
          onClick={() => {
            if (isMobile) showStoreDetails(store.storeId);
          }}
        >
          <RowFlexContainer width="100%" alignItems="center" flexDirection="row" justifyContent="between">
            <Typography type="h4">{store.storeId}</Typography>
            {isStoreVisibilityType(store.status) && (
              <StoreVisibility type={store.status} closingDate={store.closingDate} />
            )}
          </RowFlexContainer>
          {store.name && (
            <RowFlexContainer gap={[2]}>
              <Icon icon={Icons.BAG} size="small" />
              <Typography>{store.name}</Typography>
            </RowFlexContainer>
          )}
          {store.address && (
            <RowFlexContainer gap={[2]} flex={1}>
              <Icon icon={Icons.LOCATION} size="small" />
              <Typography>{store.address}</Typography>
            </RowFlexContainer>
          )}
          {!isMobile && (
            <UnderlinedTypography
              onClick={() => showStoreDetails(store.storeId)}
              data-testid="more-details"
              label={t(
                areaResultTranslations.moreDetailsText.key,
                areaResultTranslations.moreDetailsText.defaultValue,
              )}
            />
          )}
        </ColumnFlexContainer>
      ))}
    </StoresViewWrapper>
  );
};
